import { useState, useEffect } from 'react';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';
import { AppHeader } from '../components/AppHeader';
import { useSmartBack } from '../hooks/useSmartBack';
import { TierBadge } from '../components/TierBadge';
import './SeasonRewardsPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface SeasonReward {
  id: number;
  season_number: number;
  season_end: string;
  rank: number;
  final_rating: number;
  tier: string;
  badge_code: string;
}

const MEDAL = (rank: number) => (rank <= 10 ? '💎' : rank <= 100 ? '🥇' : '🥈');

export function SeasonRewardsPage() {
  const { t } = useI18n();
  const { token } = useAuth();
  const smartBack = useSmartBack('/');
  const [rewards, setRewards] = useState<SeasonReward[] | null>(null);
  const [loading, setLoading] = useState(true);
  const p = t.profile;

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/stats/season/rewards`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : { rewards: [] }))
      .then(d => setRewards(d.rewards ?? []))
      .catch(() => setRewards([]))
      .finally(() => setLoading(false));
  }, [token]);

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
  };

  return (
    <div className="season-rewards-page">
      <SEO path="/sezon-odulleri" />
      <AppHeader onBack={smartBack} backLabel={t.common.backToHome} title={p.seasonRewards} />
      <p className="sr-subtitle">{p.seasonRewardsTitle}</p>

      {loading ? (
        <p className="sr-loading">{t.common.loading}</p>
      ) : !rewards || rewards.length === 0 ? (
        <div className="sr-empty">
          <span className="sr-empty-icon">🏅</span>
          <p>{p.seasonRewardsEmpty}</p>
        </div>
      ) : (
        <div className="sr-list">
          {rewards.map((r, i) => (
            <div key={r.id} className="sr-card" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="sr-medal" aria-hidden="true">{MEDAL(r.rank)}</span>
              <div className="sr-info">
                <span className="sr-season">{p.seasonRewardSeason.replace('{n}', String(r.season_number))}</span>
                <span className="sr-meta">
                  <TierBadge rating={r.final_rating} size="sm" />
                  <span className="sr-date">{fmtDate(r.season_end)}</span>
                </span>
              </div>
              <span className="sr-rank">{p.seasonRewardRank.replace('{rank}', String(r.rank))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
